/**
 * Student Messages Panel - Vista de mensajes de feedback docente
 * Muestra mensajes personalizados enviados por docentes sobre laboratorios
 */

import React, { useState, useEffect } from 'react';
import {
  MessageSquare,
  Mail,
  Calendar,
  User as UserIcon,
  FlaskConical,
  Award,
  Clock,
  Eye,
  CheckCircle2,
  Inbox
} from 'lucide-react';
import { eplApi } from '../services/epl_api';

const StudentMessages = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [filter, setFilter] = useState('all'); // 'all', 'unread', 'read'

  useEffect(() => {
    loadMessages();
  }, []);

  const loadMessages = async () => {
    try {
      setLoading(true);
      const response = await eplApi.getMyMessages();
      setMessages(response.data || []);
    } catch (error) {
      console.error('Error cargando mensajes:', error);
      setMessages([]);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = async (messageId) => {
    try {
      await eplApi.markMessageAsRead(messageId);
      
      // Actualizar estado local
      setMessages(prev => prev.map(msg => 
        msg._id === messageId ? { ...msg, readAt: new Date() } : msg
      ));
      
    } catch (error) {
      console.error('Error marcando mensaje como leído:', error);
    }
  };

  const handleViewMessage = async (message) => {
    setSelectedMessage(message);
    
    // Marcar como leído si no lo está
    if (!message.readAt) {
      await handleMarkAsRead(message._id);
    }
  };

  const filteredMessages = messages.filter(msg => {
    if (filter === 'unread') return !msg.readAt;
    if (filter === 'read') return !!msg.readAt;
    return true;
  });

  const formatDate = (dateString) => {
    if (!dateString) return 'Hace un momento';
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);
    
    if (diffInHours < 24) {
      return `Hace ${Math.floor(diffInHours)} horas`;
    } else if (diffInHours < 48) {
      return 'Ayer';
    } else {
      return date.toLocaleDateString('es-ES', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-600 border-t-transparent"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-3 sm:p-4 md:p-6">
      <div className="max-w-5xl mx-auto">
        
        {/* Header */}
        <div className="mb-4 sm:mb-6">
          <div className="flex items-center gap-2 sm:gap-3 mb-3">
            <div className="w-9 h-9 sm:w-10 sm:h-10 bg-purple-600 rounded-full flex items-center justify-center flex-shrink-0">
              <MessageSquare className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
            </div>
            <div className="min-w-0 flex-1">
              <h1 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 leading-tight">Mensajes del Docente</h1>
              <p className="text-xs sm:text-sm text-gray-600 mt-0.5">Feedback personalizado sobre tus laboratorios</p>
            </div>
          </div>

          {/* Filtros */}
          <div className="flex gap-1.5 sm:gap-2 mt-3">
            <button
              onClick={() => setFilter('all')}
              className={`px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors flex-1 sm:flex-initial ${
                filter === 'all' 
                  ? 'bg-purple-600 text-white' 
                  : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
            >
              <span className="hidden sm:inline">Todos </span>({messages.length})
            </button>
            <button
              onClick={() => setFilter('unread')}
              className={`px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors flex-1 sm:flex-initial ${
                filter === 'unread' 
                  ? 'bg-purple-600 text-white' 
                  : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
            >
              <span className="hidden sm:inline">No leídos </span>({messages.filter(m => !m.readAt).length})
            </button>
            <button
              onClick={() => setFilter('read')}
              className={`px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors flex-1 sm:flex-initial ${
                filter === 'read' 
                  ? 'bg-purple-600 text-white' 
                  : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
            >
              <span className="hidden sm:inline">Leídos </span>({messages.filter(m => m.readAt).length})
            </button>
          </div>
        </div>

        {/* Lista de Mensajes */}
        {filteredMessages.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border p-12 text-center">
            <Inbox className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {filter === 'unread' ? 'No hay mensajes nuevos' : 'No hay mensajes'}
            </h3>
            <p className="text-gray-500">
              Aquí aparecerán los mensajes de feedback de tus docentes
            </p>
          </div>
        ) : (
          <div className="space-y-2 sm:space-y-3">
            {filteredMessages.map((message) => (
              <div
                key={message._id}
                onClick={() => handleViewMessage(message)}
                className={`bg-white rounded-lg shadow-sm border p-3 sm:p-4 cursor-pointer transition-all hover:shadow-md active:scale-[0.99] ${
                  !message.readAt ? 'border-l-4 border-l-purple-600' : ''
                }`}
              >
                <div className="flex items-start gap-2 sm:gap-3">
                  
                  {/* Icono */}
                  <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                    !message.readAt ? 'bg-purple-100' : 'bg-gray-100'
                  }`}>
                    <MessageSquare className={`w-4 h-4 sm:w-5 sm:h-5 ${
                      !message.readAt ? 'text-purple-600' : 'text-gray-500'
                    }`} />
                  </div>

                  {/* Contenido */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2 mb-1.5">
                      <div className="flex-1 min-w-0">
                        <h3 className={`text-sm sm:text-base font-semibold mb-1 leading-tight break-words ${
                          !message.readAt ? 'text-purple-900' : 'text-gray-900'
                        }`}>
                          {message.subject}
                        </h3>
                        <div className="flex items-center gap-1.5 text-xs sm:text-sm text-gray-600">
                          <UserIcon className="w-3 h-3 flex-shrink-0" />
                          <span className="truncate">{message.teacherName || 'Docente'}</span>
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <div className="text-[10px] sm:text-xs text-gray-500 flex items-center gap-0.5 sm:gap-1 whitespace-nowrap">
                          <Calendar className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                          <span className="hidden sm:inline">{formatDate(message.sentAt)}</span>
                          <span className="sm:hidden">{formatDate(message.sentAt).replace('Hace ', '')}</span>
                        </div>
                        {!message.readAt && (
                          <span className="inline-block mt-1 px-1.5 sm:px-2 py-0.5 bg-purple-100 text-purple-700 text-[10px] sm:text-xs font-medium rounded-full">
                            Nuevo
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Lab Info */}
                    <div className="flex items-center gap-2 sm:gap-3 text-xs sm:text-sm mb-1.5 flex-wrap">
                      <div className="flex items-center gap-1 text-gray-600 min-w-0">
                        <FlaskConical className="w-3 h-3 sm:w-4 sm:h-4 text-blue-600 flex-shrink-0" />
                        <span className="truncate">{message.labName}</span>
                      </div>
                      {message.labScore !== null && (
                        <div className="flex items-center gap-1 text-yellow-600 font-medium flex-shrink-0">
                          <Award className="w-3 h-3 sm:w-4 sm:h-4" />
                          <span>{message.labScore}/100</span>
                        </div>
                      )}
                    </div>

                    {/* Preview del mensaje */}
                    <p className="text-xs sm:text-sm text-gray-600 line-clamp-2 leading-relaxed break-words">
                      {message.message}
                    </p>

                    {/* Email status */}
                    {message.emailSent && (
                      <div className="flex items-center gap-1 text-xs text-green-600 mt-2">
                        <Mail className="w-3 h-3" />
                        <span>También enviado por email</span>
                      </div>
                    )}
                  </div>

                  {/* Action icon */}
                  <div className="flex-shrink-0">
                    <Eye className="w-5 h-5 text-gray-400" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Modal de Detalle */}
        {selectedMessage && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4"
            onClick={() => setSelectedMessage(null)}
          >
            <div 
              className="bg-white rounded-t-2xl sm:rounded-lg max-w-2xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="sticky top-0 bg-gradient-to-r from-purple-600 to-purple-700 text-white p-4 sm:p-6 rounded-t-2xl sm:rounded-t-lg">
                <div className="flex items-start gap-2 sm:gap-3 mb-3 sm:mb-4">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <MessageSquare className="w-5 h-5 sm:w-6 sm:h-6" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h2 className="text-base sm:text-xl font-bold leading-tight break-words">{selectedMessage.subject}</h2>
                    <div className="flex items-center gap-1.5 sm:gap-2 text-purple-100 text-xs sm:text-sm mt-1">
                      <UserIcon className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                      <span className="truncate">{selectedMessage.teacherName || 'Docente'}</span>
                    </div>
                  </div>
                </div>

                {/* Info del Lab */}
                <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-xs sm:text-sm">
                  <div className="flex items-center gap-1.5 sm:gap-2 min-w-0">
                    <FlaskConical className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                    <span className="truncate">{selectedMessage.labName}</span>
                  </div>
                  {selectedMessage.labScore !== null && (
                    <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0">
                      <Award className="w-3 h-3 sm:w-4 sm:h-4" />
                      <span className="font-semibold">{selectedMessage.labScore}/100</span>
                    </div>
                  )}
                  <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0">
                    <Calendar className="w-3 h-3 sm:w-4 sm:h-4" />
                    <span className="hidden sm:inline">{new Date(selectedMessage.sentAt).toLocaleDateString('es-ES')}</span>
                    <span className="sm:hidden">{new Date(selectedMessage.sentAt).toLocaleDateString('es-ES', { day: '2-digit', month: 'short' })}</span>
                  </div>
                </div>
              </div>

              {/* Body */}
              <div className="p-4 sm:p-6">
                <div className="prose prose-sm sm:prose-base max-w-none">
                  <div className="whitespace-pre-wrap text-gray-700 text-sm sm:text-base leading-relaxed break-words">
                    {selectedMessage.message}
                  </div>
                </div>

                {selectedMessage.emailSent && (
                  <div className="mt-4 sm:mt-6 p-3 sm:p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-2 text-xs sm:text-sm text-green-700">
                    <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <span className="leading-relaxed">Este mensaje también fue enviado a tu correo electrónico</span>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 p-3 sm:p-4">
                <button
                  onClick={() => setSelectedMessage(null)}
                  className="w-full px-4 py-3 bg-purple-600 text-white text-sm sm:text-base font-medium rounded-lg hover:bg-purple-700 active:bg-purple-800 transition-colors"
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default StudentMessages;
